import { useCallback, useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import {
  applySceneToYDoc,
  PRESENCE_MAX_SELECTION,
  presenceColor,
  presenceStateSchema,
  yDocToSceneDoc,
  Y_COMMENTS_KEY,
  Y_CONNECTORS_KEY,
  Y_META_KEY,
  Y_NODES_KEY,
  Y_USERS_KEY,
  Y_VLANS_KEY,
  type PresenceState,
  type PresenceViewport,
  type RemotePresence,
  type SceneDoc,
} from '@system-atlas/shared';

/** Origin lokaler Transaktionen: nur diese landen im eigenen Undo-Verlauf (Pro-Benutzer-Undo). */
const LOCAL_ORIGIN = 'local-edit';

/** Cursor-Updates drosseln (~40 ms, Spec Abschnitt 6): Zwischenwerte werden verworfen —
 *  bei kontinuierlichen Move-Events kommt ohnehin sofort der naechste Wert. */
const CURSOR_THROTTLE_MS = 40;

/** Viewport-Updates drosseln (leading+trailing): anders als beim Cursor zaehlt der LETZTE
 *  Wert — nach Gestenende muss der Folgende exakt auf dem Zielausschnitt stehen. */
const VIEWPORT_THROTTLE_MS = 80;

export interface CollabPresence {
  /** Andere Clients mit gueltigem Awareness-State (schema-validiert). */
  others: RemotePresence[];
  /** Eigene Zeigerposition melden (Weltkoordinaten; null = Zeiger weg). */
  setCursor: (p: { x: number; y: number } | null) => void;
  /** Eigene Auswahl melden (Objekt-IDs: Knoten und Verbinder; leer = nichts ausgewaehlt). */
  setSelection: (ids: string[]) => void;
  /** Eigenen Sichtbereich melden (Weltzentrum + Zoom; Phase 4, gedrosselt). */
  setViewport: (v: PresenceViewport) => void;
  /** Melden, wem man gerade folgt (null = niemandem; Phase 4). */
  setFollowing: (userId: string | null) => void;
}

export type CollabConnectionState = 'connecting' | 'connected' | 'disconnected' | 'denied';

export interface CollabScene {
  scene: SceneDoc;
  /** Wie useUndoable.set: Wert oder Updater; schreibt als Diff in das Y.Doc. */
  setScene: (updater: SceneDoc | ((prev: SceneDoc) => SceneDoc)) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** true, sobald der Zustand verbindlich ist (Collab: nach erstem Sync; lokal: sofort). */
  ready: boolean;
  /** 'denied' = Server hat endgueltig abgelehnt (Session weg/kein Zugriff) -> read-only. */
  connection: CollabConnectionState;
  /** Praesenz der anderen Teilnehmer + Melden der eigenen Cursor-Position. */
  presence: CollabPresence;
}

interface CollabHandles {
  doc: Y.Doc;
  undoManager: Y.UndoManager;
}

/**
 * Y.Doc als Single Source of Truth der Szene (Spec Abschnitt 3.1). Ersetzt useUndoable.
 * enabled=false (COLLAB_ENABLED aus): lokales Y.Doc ohne Provider — das alte
 * Sperrmodell/Autosave im SceneEditor bleibt dann zustaendig.
 */
export function useCollabScene(opts: {
  drawingId: string;
  initialScene: SceneDoc;
  enabled: boolean;
  /** Angemeldeter Benutzer fuer den Awareness-State (null = keine Praesenz senden). */
  localUser: { userId: string; name: string } | null;
}): CollabScene {
  const { drawingId, initialScene, enabled, localUser } = opts;
  const handlesRef = useRef<CollabHandles | null>(null);
  const awarenessRef = useRef<WebsocketProvider['awareness'] | null>(null);
  const lastCursorSentRef = useRef(0);
  const lastSelectionRef = useRef<string[]>([]);
  const lastViewportSentRef = useRef(0);
  const pendingViewportRef = useRef<PresenceViewport | null>(null);
  const viewportTimerRef = useRef<number | null>(null);
  const readyRef = useRef(!enabled);
  const [scene, setSceneState] = useState<SceneDoc>(initialScene);
  const [history, setHistory] = useState({ canUndo: false, canRedo: false });
  const [ready, setReady] = useState(!enabled);
  const [connection, setConnection] = useState<CollabConnectionState>(enabled ? 'connecting' : 'connected');
  const [others, setOthers] = useState<RemotePresence[]>([]);

  useEffect(() => {
    const doc = new Y.Doc();
    const scope = [
      doc.getMap(Y_META_KEY),
      doc.getArray(Y_NODES_KEY),
      doc.getArray(Y_CONNECTORS_KEY),
      doc.getArray(Y_VLANS_KEY),
      doc.getArray(Y_USERS_KEY),
      doc.getArray(Y_COMMENTS_KEY),
    ];
    // captureTimeout 0: jede setScene-Transaktion = genau ein Undo-Schritt (wie useUndoable).
    const undoManager = new Y.UndoManager(scope, { trackedOrigins: new Set([LOCAL_ORIGIN]), captureTimeout: 0 });
    handlesRef.current = { doc, undoManager };

    const refresh = () => {
      setSceneState(yDocToSceneDoc(doc));
      setHistory({ canUndo: undoManager.canUndo(), canRedo: undoManager.canRedo() });
    };
    doc.on('update', refresh);

    let provider: WebsocketProvider | null = null;
    if (enabled) {
      const wsBase = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/drawings`;
      // disableBc: kein BroadcastChannel-Direktsync zwischen Tabs — alles laeuft ueber den
      // Server, damit die serverseitige Rechte-Durchsetzung nicht umgangen wird.
      provider = new WebsocketProvider(wsBase, `${drawingId}/collab`, doc, { disableBc: true });

      // --- Praesenz (Phase 2): eigenen Zustand setzen, fremde sammeln -----
      const awareness = provider.awareness;
      awarenessRef.current = awareness;
      if (localUser) {
        const localState: PresenceState = {
          userId: localUser.userId,
          name: localUser.name,
          color: presenceColor(localUser.userId),
          cursor: null,
          selection: [],
          viewport: null,
          followingUserId: null,
        };
        awareness.setLocalState(localState);
      }
      const refreshPresence = () => {
        const states: RemotePresence[] = [];
        for (const [clientId, raw] of awareness.getStates()) {
          if (clientId === awareness.clientID) continue;
          const parsed = presenceStateSchema.safeParse(raw);
          if (parsed.success) states.push({ ...parsed.data, clientId });
        }
        setOthers(states);
      };
      awareness.on('change', refreshPresence);
      refreshPresence();

      provider.on('status', ({ status }: { status: string }) => {
        if (status === 'connected') setConnection('connected');
        else if (status === 'disconnected') setConnection((prev) => (prev === 'denied' ? prev : 'disconnected'));
      });
      provider.on('sync', (isSynced: boolean) => {
        if (isSynced) {
          readyRef.current = true;
          setReady(true);
          refresh();
        }
      });
      // Vom Server bewusst geschlossen (4xxx: Session weg, kein Zugriff, Origin):
      // nicht endlos neu verbinden, sondern read-only schalten.
      provider.on('connection-close', (event: CloseEvent | null) => {
        if (event && event.code >= 4400 && event.code < 4500) {
          setConnection('denied');
          provider?.disconnect();
        }
      });
    } else {
      // Flag aus: lokal aus der geladenen Szene befuellen. Origin 'seed' ist nicht
      // getrackt -> das Befuellen landet nicht im Undo-Verlauf.
      applySceneToYDoc(doc, initialScene, 'seed');
      refresh();
    }

    return () => {
      handlesRef.current = null;
      readyRef.current = !enabled;
      awarenessRef.current = null;
      lastSelectionRef.current = [];
      if (viewportTimerRef.current !== null) window.clearTimeout(viewportTimerRef.current);
      viewportTimerRef.current = null;
      pendingViewportRef.current = null;
      setOthers([]);
      provider?.destroy();
      undoManager.destroy();
      doc.destroy();
    };
    // initialScene/localUser zaehlen bewusst nur beim (Re-)Mount — der Server ist danach
    // die Quelle bzw. der Benutzer wechselt waehrend einer Editor-Sitzung nicht.
  }, [drawingId, enabled]);

  const setScene = useCallback((updater: SceneDoc | ((prev: SceneDoc) => SceneDoc)) => {
    const handles = handlesRef.current;
    // Vor dem ersten Sync nicht schreiben: ein befuelltes lokales Doc wuerde beim Merge
    // mit dem Server-Stand Inhalte duplizieren.
    if (!handles || !readyRef.current) return;
    const prev = yDocToSceneDoc(handles.doc);
    const next = typeof updater === 'function' ? updater(prev) : updater;
    if (next === prev) return;
    applySceneToYDoc(handles.doc, next, LOCAL_ORIGIN);
  }, []);

  const undo = useCallback(() => handlesRef.current?.undoManager.undo(), []);
  const redo = useCallback(() => handlesRef.current?.undoManager.redo(), []);

  const setCursor = useCallback((p: { x: number; y: number } | null) => {
    const awareness = awarenessRef.current;
    if (!awareness || awareness.getLocalState() === null) return;
    if (p === null) {
      // Zeiger verlaesst den Canvas: sofort melden (kein Throttle), sonst "haengt" der Cursor.
      awareness.setLocalStateField('cursor', null);
      return;
    }
    const now = Date.now();
    if (now - lastCursorSentRef.current < CURSOR_THROTTLE_MS) return;
    lastCursorSentRef.current = now;
    awareness.setLocalStateField('cursor', { x: p.x, y: p.y });
  }, []);

  const setSelection = useCallback((ids: string[]) => {
    const awareness = awarenessRef.current;
    if (!awareness || awareness.getLocalState() === null) return;
    // Obergrenze: riesige Marquee-Auswahlen nicht komplett in den Awareness-State kippen.
    const capped = ids.length > PRESENCE_MAX_SELECTION ? ids.slice(0, PRESENCE_MAX_SELECTION) : ids;
    // Identische Auswahl nicht erneut senden (der Melde-Effekt feuert bei jedem Render der Liste).
    const last = lastSelectionRef.current;
    if (capped.length === last.length && capped.every((id, i) => id === last[i])) return;
    lastSelectionRef.current = capped;
    awareness.setLocalStateField('selection', capped);
  }, []);

  const setViewport = useCallback((v: PresenceViewport) => {
    const awareness = awarenessRef.current;
    if (!awareness || awareness.getLocalState() === null) return;
    pendingViewportRef.current = v;
    if (viewportTimerRef.current !== null) return; // Trailing-Send ist bereits geplant
    const send = () => {
      viewportTimerRef.current = null;
      lastViewportSentRef.current = Date.now();
      const pending = pendingViewportRef.current;
      if (pending && awarenessRef.current) awarenessRef.current.setLocalStateField('viewport', pending);
    };
    const elapsed = Date.now() - lastViewportSentRef.current;
    if (elapsed >= VIEWPORT_THROTTLE_MS) send();
    else viewportTimerRef.current = window.setTimeout(send, VIEWPORT_THROTTLE_MS - elapsed);
  }, []);

  const setFollowing = useCallback((userId: string | null) => {
    const awareness = awarenessRef.current;
    if (!awareness || awareness.getLocalState() === null) return;
    awareness.setLocalStateField('followingUserId', userId);
  }, []);

  return {
    scene,
    setScene,
    undo,
    redo,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    ready,
    connection,
    presence: { others, setCursor, setSelection, setViewport, setFollowing },
  };
}
